import React, { useState, useEffect, useRef } from "react";

import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  Film,
  Tv,
  TrendingUp,
  Shuffle,
  Sun,
  Moon,
  Menu,
  Home,
  Star,
  Zap,
  Settings,
  LogIn,
  LogOut,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import LeftMenu from "../components/LeftMenu";
import MoviesCarousel from "../components/MoviesCarousel";
import SeriesCarousel from "../components/SeriesCarousel";
import SmartAppBanner from "../components/SmartAppBanner";
import Top10Carousel from "../components/Top10Carousel";
import apiService from "../services/api";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerDiscover = () => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Auth state
  const isAuthenticated = !!webPlayerStorage.getToken();
  const profile = JSON.parse(sessionStorage.getItem("selectedProfile") || "{}");
  const playlist = JSON.parse(localStorage.getItem("selectedPlaylist") || "{}");

  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAllResults, setShowAllResults] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("discoverDarkMode");

    return saved ? JSON.parse(saved) : false;
  });

  // Mobile menu items
  const mobileMenuItems = [
    { icon: Search, label: "Search", id: "search", requiresAuth: false },
    { icon: Home, label: "Home", id: "home", requiresAuth: false },
    { icon: Star, label: "Favorites", id: "watchlist", requiresAuth: true },
    { icon: Zap, label: "Live TV", id: "live", requiresAuth: true },
    { icon: Film, label: "Movies", id: "movies", requiresAuth: false },
    { icon: Tv, label: "Series", id: "series", requiresAuth: false },
    { icon: Settings, label: "Settings", id: "settings", requiresAuth: true },
  ];

  const handleMobileMenuClick = (menuId) => {
    setIsMobileMenuOpen(false);
    switch (menuId) {
      case "search":
        navigate("/web-player/search");
        break;
      case "home":
        navigate("/web-player/home");
        break;
      case "watchlist":
        navigate("/web-player/watchlist");
        break;
      case "live":
        navigate("/web-player/live-tv");
        break;
      case "movies":
        navigate("/web-player/movies");
        break;
      case "series":
        navigate("/web-player/series");
        break;
      case "settings":
        navigate("/web-player/settings");
        break;
      case "login":
        navigate("/web-player/login");
        break;
      case "logout":
        webPlayerStorage.clearAll();
        navigate("/web-player/home");
        window.location.reload();
        break;
      default:
        break;
    }
  };

  // Content State
  const [trendingMixed, setTrendingMixed] = useState([]);
  const [popularMovies, setPopularMovies] = useState([]);
  const [popularSeries, setPopularSeries] = useState([]);
  const [randomMovies, setRandomMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Catchy taglines for rotation
  const taglines = [
    "Discover What to Watch Next",
    "Your Next Favorite Awaits",
    "Endless Entertainment, One Search",
    "Find Your Perfect Watch",
  ];
  const [currentTagline] = useState(taglines[0]);

  // Load content on mount
  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      try {
        // Fetch home data which includes trending
        const homeResponse = await apiService.webPlayerGetHome();
        const homeData = homeResponse?.data || homeResponse;

        // Create trending mixed from carousel items
        if (homeData?.carousel) {
          setTrendingMixed(homeData.carousel.slice(0, 10));
        }

        // Fetch movies
        const moviesResponse = await apiService.webPlayerGetMovies({
          limit: 20,
        });
        const moviesData = moviesResponse?.data || moviesResponse;

        if (Array.isArray(moviesData)) {
          setPopularMovies(moviesData.slice(0, 15));
          // Shuffle for random section
          const shuffled = [...moviesData].sort(() => Math.random() - 0.5);

          setRandomMovies(shuffled.slice(0, 15));
        } else if (moviesData?.movies) {
          setPopularMovies(moviesData.movies.slice(0, 15));
          const shuffled = [...moviesData.movies].sort(
            () => Math.random() - 0.5
          );

          setRandomMovies(shuffled.slice(0, 15));
        }

        // Fetch series
        const seriesResponse = await apiService.webPlayerGetSeries({
          limit: 15,
        });
        const seriesData = seriesResponse?.data || seriesResponse;

        if (Array.isArray(seriesData)) {
          setPopularSeries(seriesData.slice(0, 15));
        } else if (seriesData?.series) {
          setPopularSeries(seriesData.series.slice(0, 15));
        }
      } catch (error) {
        console.error("Error loading discover content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, []);

  // Handle search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.length >= 3) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await apiService.webPlayerSearch({
            term: searchQuery,
            limit: 20,
          });

          if (response?.data) {
            setSearchResults(response.data);
          }
        } catch (error) {
          console.error("Search error:", error);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchInput = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowAllResults(false);
    searchInputRef.current?.focus();
  };

  const handleViewAllResults = () => {
    setShowAllResults(true);
    setIsSearchFocused(false);
  };

  const handleResultClick = (item) => {
    const mediaType = item.mediaType || item.type;
    // Use mediaId, tmdbId, or id - skip if -1 or invalid
    const mediaId =
      item.mediaId && item.mediaId !== -1
        ? item.mediaId
        : item.tmdbId || item.id;

    if (mediaId && mediaId !== -1) {
      if (mediaType === "movie") {
        navigate(`/web-player/movie/${mediaId}`);
      } else if (mediaType === "series" || mediaType === "tv") {
        navigate(`/web-player/series/${mediaId}`);
      }
    }
  };

  const handleTop10Click = (item) => {
    const mediaType = item.mediaType || item.type;
    // Use mediaId, tmdbId, or id - skip if -1 or invalid
    const mediaId =
      item.mediaId && item.mediaId !== -1
        ? item.mediaId
        : item.tmdbId || item.id;

    if (mediaId && mediaId !== -1) {
      if (mediaType === "movie") {
        navigate(`/web-player/movie/${mediaId}`);
      } else if (mediaType === "series" || mediaType === "tv") {
        navigate(`/web-player/series/${mediaId}`);
      }
    }
  };

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;

    setIsDarkMode(newMode);
    localStorage.setItem("discoverDarkMode", JSON.stringify(newMode));
  };

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={
          isDarkMode
            ? {
                backgroundColor: "#003153",
                backgroundImage:
                  "linear-gradient(315deg, #003153 0%, #1B1B1B 74%)",
                backgroundAttachment: "fixed",
              }
            : {
                backgroundImage:
                  "linear-gradient(112.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(157.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(135deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(90deg, rgb(195, 195, 195),rgb(228, 228, 228))",
                backgroundBlendMode: "overlay,overlay,overlay,normal",
                backgroundAttachment: "fixed",
              }
        }
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4" />
          <p
            className={`text-lg ${isDarkMode ? "text-white" : "text-gray-700"}`}
          >
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Discover - Lunova Web Player</title>
        <meta
          content="Discover movies and TV shows. Find trending entertainment with detailed information, cast, and reviews."
          name="description"
        />
        <meta content="Discover - Lunova Web Player" property="og:title" />
        <meta
          content="Discover movies and TV shows. Find trending entertainment."
          property="og:description"
        />
      </Helmet>

      <SmartAppBanner />

      <div
        className="min-h-screen relative transition-colors duration-500"
        style={
          isDarkMode
            ? {
                backgroundColor: "#003153",
                backgroundImage:
                  "linear-gradient(315deg, #003153 0%, #1B1B1B 74%)",
                backgroundAttachment: "fixed",
              }
            : {
                backgroundImage:
                  "linear-gradient(112.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(157.5deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(135deg, rgb(214, 214, 214) 0%, rgb(214, 214, 214) 10%,rgb(195, 195, 195) 10%, rgb(195, 195, 195) 53%,rgb(176, 176, 176) 53%, rgb(176, 176, 176) 55%,rgb(157, 157, 157) 55%, rgb(157, 157, 157) 60%,rgb(137, 137, 137) 60%, rgb(137, 137, 137) 88%,rgb(118, 118, 118) 88%, rgb(118, 118, 118) 91%,rgb(99, 99, 99) 91%, rgb(99, 99, 99) 100%),linear-gradient(90deg, rgb(195, 195, 195),rgb(228, 228, 228))",
                backgroundBlendMode: "overlay,overlay,overlay,normal",
                backgroundAttachment: "fixed",
              }
        }
      >
        {/* Mobile Hamburger Menu Button */}
        <button
          className={`fixed top-4 left-4 z-50 hidden p-3 rounded-full transition-all duration-300 hover:scale-110 ${
            isDarkMode
              ? "bg-gray-800 text-white hover:bg-gray-700"
              : "bg-white/80 text-gray-700 hover:bg-white shadow-lg"
          }`}
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Mobile Menu - iOS Bubble Effect */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              animate={{
                clipPath: "circle(150% at 40px 40px)",
              }}
              className={`fixed inset-0 z-[100] lg:hidden flex flex-col ${
                isDarkMode
                  ? "bg-gray-900/98 backdrop-blur-xl"
                  : "bg-white/98 backdrop-blur-xl"
              }`}
              exit={{
                clipPath: "circle(0% at 40px 40px)",
              }}
              initial={{
                clipPath: "circle(0% at 40px 40px)",
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 40,
              }}
            >
              {/* Close Button - Same position as hamburger */}
              <button
                className={`absolute top-4 left-4 z-50 p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                  isDarkMode
                    ? "bg-gray-800 text-white hover:bg-gray-700"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Menu Content */}
              <div className="flex flex-col h-full pt-20 pb-8 px-6">
                {/* Logo and Title */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-4 mb-8"
                  initial={{ opacity: 0, y: -20 }}
                  transition={{ delay: 0.1 }}
                >
                  <img
                    alt="Lunova"
                    className="w-12 h-12 rounded-xl"
                    src="/logo.png"
                  />
                  <span
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
                  >
                    Lunova
                  </span>
                </motion.div>

                {/* Menu Items with stagger animation */}
                <div className="flex-1 space-y-2">
                  {mobileMenuItems
                    .filter((item) => !item.requiresAuth || isAuthenticated)
                    .map((item, index) => (
                      <motion.button
                        animate={{ opacity: 1, x: 0 }}
                        className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${
                          isDarkMode
                            ? "text-white hover:bg-white/10"
                            : "text-gray-800 hover:bg-gray-100"
                        }`}
                        initial={{ opacity: 0, x: -30 }}
                        key={item.id}
                        transition={{ delay: 0.05 * (index + 1) }}
                        onClick={() => handleMobileMenuClick(item.id)}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isDarkMode ? "bg-white/10" : "bg-gray-100"
                          }`}
                        >
                          <item.icon className="w-5 h-5" />
                        </div>
                        <span className="text-lg font-medium">
                          {item.label}
                        </span>
                      </motion.button>
                    ))}
                </div>

                {/* Login/Logout Button */}
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="pt-4 border-t border-gray-700/20"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ delay: 0.3 }}
                >
                  {isAuthenticated ? (
                    <button
                      className="w-full flex items-center gap-4 px-4 py-4 text-red-500 hover:bg-red-500/10 rounded-xl transition-colors"
                      onClick={() => handleMobileMenuClick("logout")}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-500/10">
                        <LogOut className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium">Logout</span>
                    </button>
                  ) : (
                    <button
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl transition-colors ${
                        isDarkMode
                          ? "text-blue-400 hover:bg-blue-500/10"
                          : "text-blue-600 hover:bg-blue-500/10"
                      }`}
                      onClick={() => handleMobileMenuClick("login")}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isDarkMode ? "bg-blue-500/10" : "bg-blue-100"
                        }`}
                      >
                        <LogIn className="w-5 h-5" />
                      </div>
                      <span className="text-lg font-medium">Login</span>
                    </button>
                  )}
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Theme Toggle Button */}
        <button
          className={`fixed top-4 right-4 z-50 p-3 rounded-full transition-all duration-300 hover:scale-110 ${
            isDarkMode
              ? "bg-gray-800 text-yellow-400 hover:bg-gray-700"
              : "bg-white/80 text-gray-700 hover:bg-white shadow-lg"
          }`}
          onClick={toggleDarkMode}
        >
          {isDarkMode ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>

        {/* Dashboard Button */}
        <button
          className={`fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 text-xs sm:text-sm font-medium px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-60 hover:opacity-100 hover:scale-105 transition-all duration-300 ${
            isDarkMode
              ? "text-white bg-black/50"
              : "text-gray-700 bg-white/70 shadow-md"
          }`}
          onClick={() => {
            webPlayerStorage.clearAll();
            window.location.href = "https://dashboard.lunova.app";
          }}
        >
          Dashboard
        </button>

        {/* Left Menu */}
        <LeftMenu
          activeMenuItem="discover"
          isDarkMode={isDarkMode}
          playlist={playlist}
          profile={profile}
        />

        {/* Main Content */}
        <div className="ml-0 lg:ml-20 min-h-screen">
          {/* Hero Section - Extra top padding on mobile for hamburger menu */}
          <div className="relative pt-20 sm:pt-16 pb-6 sm:pb-8 px-4 sm:px-8 lg:px-16">
            <div className="relative z-10 max-w-4xl mx-auto text-center">
              {/* Tagline - Bigger on mobile */}
              <motion.h1
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className={`bg-clip-text text-transparent ${
                    isDarkMode
                      ? "bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400"
                      : "bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
                  }`}
                >
                  {currentTagline}
                </span>
              </motion.h1>

              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className={`text-base sm:text-lg mb-6 sm:mb-8 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Search thousands of movies and series
              </motion.p>

              {/* Search Bar */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div
                  className={`relative flex items-center backdrop-blur-md rounded-full border-2 transition-all duration-300 ${
                    isDarkMode
                      ? `bg-gray-800/80 ${isSearchFocused ? "border-blue-500 shadow-lg shadow-blue-500/20" : "border-gray-700/50"}`
                      : `bg-white/80 shadow-md shadow-gray-300/50 ${isSearchFocused ? "border-blue-500 shadow-blue-400/30" : "border-[#dee0e4]"}`
                  }`}
                >
                  <Search
                    className={`absolute left-4 sm:left-5 w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  />
                  <input
                    className={`w-full pl-11 sm:pl-14 pr-11 sm:pr-14 py-3 sm:py-4 bg-transparent text-base sm:text-lg rounded-full border-0 focus:outline-none focus:ring-0 ${
                      isDarkMode
                        ? "text-white placeholder-gray-500"
                        : "text-gray-800 placeholder-gray-400"
                    }`}
                    placeholder="Search movies and series..."
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onBlur={() => setIsSearchFocused(false)}
                    onChange={handleSearchInput}
                    onFocus={() => setIsSearchFocused(true)}
                  />
                  <AnimatePresence>
                    {searchQuery && (
                      <motion.button
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute right-4 sm:right-5 text-gray-400 hover:text-white transition-colors"
                        exit={{ opacity: 0, scale: 0.8 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        onClick={clearSearch}
                      >
                        <X className="w-4 h-4 sm:w-5 sm:h-5" />
                      </motion.button>
                    )}
                  </AnimatePresence>
                </div>

                {/* Search Results Dropdown */}
                <AnimatePresence>
                  {searchQuery.length >= 3 && !showAllResults && (
                    <motion.div
                      animate={{ opacity: 1, y: 0 }}
                      className={`absolute top-full left-0 right-0 mt-2 backdrop-blur-md rounded-2xl border overflow-hidden shadow-2xl z-50 ${
                        isDarkMode
                          ? "bg-gray-900/95 border-gray-700/50"
                          : "bg-white/95 border-gray-200"
                      }`}
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: -10 }}
                    >
                      {isSearching ? (
                        <div className="p-8 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
                        </div>
                      ) : searchResults.length > 0 ? (
                        <>
                          <div className="max-h-96 overflow-y-auto">
                            {searchResults.slice(0, 8).map((item, index) => (
                              <button
                                className={`w-full flex items-center gap-4 p-4 transition-colors text-left ${
                                  isDarkMode
                                    ? "hover:bg-gray-800/50"
                                    : "hover:bg-gray-100"
                                }`}
                                key={item.mediaId || item.id || index}
                                onClick={() => handleResultClick(item)}
                              >
                                {/* Poster */}
                                <div
                                  className={`w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 ${isDarkMode ? "bg-gray-800" : "bg-gray-200"}`}
                                >
                                  {item.posterPath ? (
                                    <img
                                      alt={item.name || item.title}
                                      className="w-full h-full object-cover"
                                      src={item.posterPath}
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Film className="w-6 h-6 text-gray-600" />
                                    </div>
                                  )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <h4
                                    className={`font-medium truncate ${isDarkMode ? "text-white" : "text-gray-800"}`}
                                  >
                                    {item.name || item.title}
                                  </h4>
                                  <div
                                    className={`flex items-center gap-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    <span className="capitalize">
                                      {item.mediaType || item.type}
                                    </span>
                                    {item.year && (
                                      <>
                                        <span>•</span>
                                        <span>{item.year}</span>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Type Icon */}
                                {(item.mediaType === "movie" ||
                                  item.type === "movie") && (
                                  <Film className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                )}
                                {(item.mediaType === "series" ||
                                  item.mediaType === "tv" ||
                                  item.type === "series") && (
                                  <Tv className="w-5 h-5 text-purple-400 flex-shrink-0" />
                                )}
                              </button>
                            ))}
                          </div>

                          {/* View All Button */}
                          <button
                            className={`w-full p-4 text-center text-blue-500 transition-colors border-t ${
                              isDarkMode
                                ? "hover:bg-gray-800/50 border-gray-700/50"
                                : "hover:bg-gray-100 border-gray-200"
                            }`}
                            onClick={handleViewAllResults}
                          >
                            View all {searchResults.length} results
                          </button>
                        </>
                      ) : (
                        <div
                          className={`p-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          No results found for "{searchQuery}"
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="px-4 sm:px-8 pb-20">
            {/* Search Results Grid - Shows when viewing all results */}
            {showAllResults && searchResults.length > 0 && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header with result count and clear X */}
                <div className="flex items-center gap-3 mb-6">
                  <button
                    className={`p-2 rounded-full transition-all hover:scale-110 ${
                      isDarkMode
                        ? "text-gray-400 hover:text-white hover:bg-gray-800"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                    }`}
                    onClick={clearSearch}
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  <h2
                    className={`text-xl sm:text-2xl font-semibold ${isDarkMode ? "text-white" : "text-gray-800"}`}
                  >
                    {searchResults.length} results for "{searchQuery}"
                  </h2>
                </div>

                {/* Results Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
                  {searchResults.map((item, index) => {
                    const image = item.posterPath || item.poster;
                    const mediaType = item.mediaType || item.type;

                    return (
                      <div
                        className="cursor-pointer"
                        key={item.mediaId || item.id || index}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleResultClick(item)}
                        onKeyDown={activateOnEnterOrSpace}
                      >
                        <div className="relative group">
                          <div
                            className="lunova-card relative card-radius-poster"
                            style={{
                              aspectRatio: "2 / 3",
                              boxShadow: "0 15px 35px -10px rgba(0,0,0,0.5)",
                            }}
                          >
                            <div className="lunova-card-clip">
                              {image ? (
                                <img
                                  alt={item.name || item.title}
                                  className="w-full h-full object-cover pointer-events-none"
                                  draggable={false}
                                  src={image}
                                  onDragStart={(e) => e.preventDefault()}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    e.currentTarget.parentElement.innerHTML = `<div class="flex items-center justify-center h-full bg-gray-800"><i class="fas fa-${mediaType === "movie" ? "film" : "tv"} text-gray-600 text-3xl"></i></div>`;
                                  }}
                                />
                              ) : (
                                <div
                                  className={`flex items-center justify-center h-full ${isDarkMode ? "bg-gray-800" : "bg-gray-300"}`}
                                >
                                  {mediaType === "movie" ? (
                                    <Film className="w-8 h-8 text-gray-500" />
                                  ) : (
                                    <Tv className="w-8 h-8 text-gray-500" />
                                  )}
                                </div>
                              )}

                              {/* Type Badge */}
                              <div className="absolute top-2 right-2">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    mediaType === "movie"
                                      ? "bg-blue-500/80 text-white"
                                      : "bg-purple-500/80 text-white"
                                  }`}
                                >
                                  {mediaType === "movie" ? "Movie" : "Series"}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Title and Year */}
                          <div className="mt-2">
                            <p
                              className={`text-sm font-medium truncate ${isDarkMode ? "text-white" : "text-gray-800"}`}
                            >
                              {item.name || item.title}
                            </p>
                            {item.year && (
                              <p
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {item.year}
                              </p>
                            )}
                          </div>

                          {/* Rotten Tomatoes Ratings */}
                          {item.rottenTomatoes && (
                            <div className="flex justify-center mt-2 select-none gap-3">
                              {item.rottenTomatoes.critics?.score &&
                                item.rottenTomatoes.critics.score !== "0" && (
                                  <div className="flex items-center gap-1">
                                    {item.rottenTomatoes.critics.icon ? (
                                      <img
                                        alt="Critics rating"
                                        className="w-4 h-4 object-contain"
                                        src={item.rottenTomatoes.critics.icon}
                                      />
                                    ) : (
                                      <span className="text-sm">🍅</span>
                                    )}
                                    <span
                                      className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}
                                    >
                                      {item.rottenTomatoes.critics.score}%
                                    </span>
                                  </div>
                                )}
                              {item.rottenTomatoes.audience?.score &&
                                item.rottenTomatoes.audience.score !== "0" && (
                                  <div className="flex items-center gap-1">
                                    {item.rottenTomatoes.audience.icon ? (
                                      <img
                                        alt="Audience rating"
                                        className="w-4 h-4 object-contain"
                                        src={item.rottenTomatoes.audience.icon}
                                      />
                                    ) : (
                                      <span className="text-sm">🍿</span>
                                    )}
                                    <span
                                      className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}
                                    >
                                      {item.rottenTomatoes.audience.score}%
                                    </span>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Top 10 Trending - Hide when showing search results */}
            {!showAllResults && trendingMixed.length > 0 && (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="flex items-center gap-2 sm:gap-3 pl-2 sm:pl-6 mb-2">
                  <TrendingUp
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? "text-red-500" : "text-red-600"}`}
                  />
                  <span
                    className={`text-sm sm:text-base font-semibold ${isDarkMode ? "text-red-500" : "text-red-600"}`}
                  >
                    TRENDING NOW
                  </span>
                </div>
                <Top10Carousel
                  isDarkMode={isDarkMode}
                  items={trendingMixed.map((item) => ({
                    ...item,
                    mediaId: item.mediaId || item.id,
                    posterPath: item.posterPath || item.poster,
                    name: item.name || item.title,
                  }))}
                  title=""
                  onItemClick={handleTop10Click}
                />
              </motion.div>
            )}

            {/* Popular Movies - Hide when showing search results */}
            {!showAllResults && popularMovies.length > 0 && (
              <motion.section
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 overflow-visible"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <h2
                  className={`text-xl sm:text-2xl font-semibold pl-2 sm:pl-6 pb-2 sm:pb-3.5 flex items-center gap-2 sm:gap-3 ${isDarkMode ? "text-white" : "text-gray-800"}`}
                >
                  <Film
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                  />
                  Popular Movies
                </h2>
                <div
                  className="pl-2 sm:pl-6 pr-4 sm:pr-8 py-2 sm:py-4"
                  style={{ overflow: "visible" }}
                >
                  <MoviesCarousel
                    isDarkMode={isDarkMode}
                    items={popularMovies.map((movie) => ({
                      id: movie.mediaId || movie.tmdbId || movie.id,
                      mediaId: movie.mediaId,
                      tmdbId: movie.tmdbId,
                      title: movie.name || movie.title,
                      poster: movie.posterPath || movie.poster,
                      backdrop: movie.backdropPath,
                      genre: movie.genres,
                      year: movie.year,
                      type: "movie",
                      mediaType: "movie",
                      rating: movie.rating,
                      rottenTomatoes: movie.rottenTomatoes,
                    }))}
                  />
                </div>
              </motion.section>
            )}

            {/* Popular Series - Hide when showing search results */}
            {!showAllResults && popularSeries.length > 0 && (
              <motion.section
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 overflow-visible"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <h2
                  className={`text-xl sm:text-2xl font-semibold pl-2 sm:pl-6 pb-2 sm:pb-3.5 flex items-center gap-2 sm:gap-3 ${isDarkMode ? "text-white" : "text-gray-800"}`}
                >
                  <Tv
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}
                  />
                  Popular Series
                </h2>
                <div
                  className="pl-2 sm:pl-6 pr-4 sm:pr-8 py-2 sm:py-4"
                  style={{ overflow: "visible" }}
                >
                  <SeriesCarousel
                    isDarkMode={isDarkMode}
                    items={popularSeries.map((series) => ({
                      id: series.mediaId || series.tmdbId || series.id,
                      mediaId: series.mediaId,
                      tmdbId: series.tmdbId,
                      title: series.name || series.title,
                      poster: series.posterPath || series.poster,
                      backdrop: series.backdropPath,
                      genre: series.genres,
                      year: series.year,
                      type: "series",
                      mediaType: "series",
                      rating: series.rating,
                      rottenTomatoes: series.rottenTomatoes,
                    }))}
                  />
                </div>
              </motion.section>
            )}

            {/* Random Picks - Hide when showing search results */}
            {!showAllResults && randomMovies.length > 0 && (
              <motion.section
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 overflow-visible"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                <h2
                  className={`text-xl sm:text-2xl font-semibold pl-2 sm:pl-6 pb-2 sm:pb-3.5 flex items-center gap-2 sm:gap-3 ${isDarkMode ? "text-white" : "text-gray-800"}`}
                >
                  <Shuffle
                    className={`w-5 h-5 sm:w-6 sm:h-6 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                  />
                  Random Picks
                </h2>
                <div
                  className="pl-2 sm:pl-6 pr-4 sm:pr-8 py-2 sm:py-4"
                  style={{ overflow: "visible" }}
                >
                  <MoviesCarousel
                    isDarkMode={isDarkMode}
                    items={randomMovies.map((movie) => ({
                      id: movie.mediaId || movie.tmdbId || movie.id,
                      mediaId: movie.mediaId,
                      tmdbId: movie.tmdbId,
                      title: movie.name || movie.title,
                      poster: movie.posterPath || movie.poster,
                      backdrop: movie.backdropPath,
                      genre: movie.genres,
                      year: movie.year,
                      type: "movie",
                      mediaType: "movie",
                      rating: movie.rating,
                      rottenTomatoes: movie.rottenTomatoes,
                    }))}
                  />
                </div>
              </motion.section>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default WebPlayerDiscover;
